import { computed, makeObservable } from 'mobx';
import { z } from 'zod';
import {
  ConfigOf,
  Connection,
  Domain,
  GetterReturnOf,
  MutableOf,
  StateOf,
} from './base';
import { DeltaBase } from './deltas';

// Reserve "type" for record_union
type ReservedFieldKeys = 'mutable' | 'oid' | 'state' | 'type';
export type RecordFields = Record<
  Exclude<string, ReservedFieldKeys>,
  Domain<any>
>;

type RecordConfig<F extends RecordFields> = {
  mutableBase: {
    -readonly [K in keyof F]: GetterReturnOf<ConfigOf<F[K]>>;
  };
  state: {
    readonly [K in keyof F]: StateOf<ConfigOf<F[K]>>;
  } & {
    readonly mutable?: false;
  };
  delta: DeltaBase; // no deltas
};

/**
 * Record
 *
 * A collection of other domains, e.g.:
 *
 *   new RecordDomain({
 *    count: new AtomDomain(z.number()),
 *    name: new AtomDomain(z.string()),
 *   })
 *
 */
export class RecordDomain<F extends RecordFields>
  implements Domain<RecordConfig<F>>
{
  private readonly domainSymbol = Symbol('record');

  constructor(private readonly fields: F) {}

  readonly stateSchema = z.object(
    Object.fromEntries(
      Object.entries(this.fields).map(([k, domain]) => [k, domain.stateSchema]),
    ),
  ) as Domain<RecordConfig<F>>['stateSchema'];

  create(oid: string, initialState: StateOf<RecordConfig<F>>, cxn: Connection) {
    const fieldMutables = Object.fromEntries(
      Object.entries(this.fields).map(([k, domain]) => [
        k,
        domain.create(oid + '.' + k, initialState[k], cxn),
      ]),
    );
    const object = makeObservable(
      {
        mutable: true,
        oid,
        get state() {
          return Object.fromEntries(
            Object.entries(fieldMutables).map(([k, m]) => [k, m.state]),
          );
        },
        get visibleOids(): string[] {
          return [
            this.oid,
            ...Object.values(fieldMutables).flatMap((m) => m.visibleOids),
          ];
        },
        wouldChange() {
          // The record domain doesn't support deltas, so it can't change
          return false;
        },
        [FIELD_MUTABLES_SYMBOL]: fieldMutables,
        [this.domainSymbol]: true,
      },
      {
        state: computed,
        visibleOids: computed,
      },
    ) as any as MutableOf<RecordConfig<F>>;
    for (const [k, mutable] of Object.entries(fieldMutables)) {
      const domain = this.fields[k];
      Object.defineProperty(object, k, {
        get() {
          return domain.getter(mutable);
        },
        set(v) {
          if (!domain.setter) {
            throw new Error(`Field ${k} is not settable (mutate it instead)`);
          }
          domain.setter(mutable, v);
        },
      });
    }
    return object;
  }

  getter(m: MutableOf<RecordConfig<F>>) {
    return m;
  }

  statesEqual(a: StateOf<RecordConfig<F>>, b: StateOf<RecordConfig<F>>) {
    for (const [k, domain] of Object.entries(this.fields)) {
      if (!domain.statesEqual(a[k], b[k])) {
        return false;
      }
    }
    return true;
  }

  isMutable(m: unknown): m is MutableOf<RecordConfig<F>> {
    return (
      typeof m === 'object' && m != null && m.hasOwnProperty(this.domainSymbol)
    );
  }
}

const FIELD_MUTABLES_SYMBOL = Symbol('fieldMutables');
